import { DateTime } from 'scaleflow/utils/date-time';

const AMZ_DATE_FORMAT = 'YYYYMMDD[T]HHmmss[Z]';

/**
 * The instant a presigned URL stops working, or `null` when that cannot be
 * determined: no URL at all, one that will not parse, or one missing the
 * `X-Amz-Date` / `X-Amz-Expires` signing params.
 *
 * A `null` return means "treat as unusable" — there is no expiry to count down
 * to, so callers should present the link as already gone rather than guess.
 */
export function s3PresignedUrlExpiresAt(
  url: string | null | undefined,
): Date | null {
  if (!url) {
    return null;
  }

  let parsedUrl;
  try {
    parsedUrl = new URL(url);
  } catch {
    return null;
  }

  const amzDate = parsedUrl.searchParams.get('X-Amz-Date');
  const amzExpires = parsedUrl.searchParams.get('X-Amz-Expires');

  if (!amzDate || !amzExpires) {
    return null;
  }

  const issuedAt = DateTime.parseUtc(amzDate, AMZ_DATE_FORMAT, true);
  const expiresSeconds = Number(amzExpires);

  if (!issuedAt.isValid() || !Number.isFinite(expiresSeconds)) {
    return null;
  }

  return issuedAt.add(expiresSeconds, 'seconds').toDate();
}

export default function s3PresignedUrlExpired(
  url: string | null | undefined,
): boolean {
  const expiresAt = s3PresignedUrlExpiresAt(url);

  // Anything we cannot read an expiry off is already unusable.
  if (!expiresAt) {
    return true;
  }

  return DateTime.utcNow().isSameOrAfter(expiresAt);
}
